import { ItemBar } from "components/ItemBar";
import { ToastContext } from "context/ToastContext";
import { prepareCodesForSubmit, CodeRangeError } from "helpers/codeRanges";
import { Button } from "primereact/button";
import { useContext } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  useApplyCaseRatePropagationMutation,
  useLazyGetCaseRatePropagationPreviewQuery,
  useUpdateCaseRateRulesetMutation,
} from "store/queries/pricing";

import { mapCaseRateFormToRequestBody } from "../helpers/mapFormToRequestBody";
import { useCaseRateCptConflicts } from "../hooks/useCaseRateCptConflicts";
import { usePropagateAfterEdit } from "./usePropagateAfterEdit";

function extractServerErrorMessage(error: unknown): string | null {
  if (!error || typeof error !== "object") return null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, message } = error as any;
  if (typeof data === "string") return data;
  if (data && typeof data === "object") {
    if (typeof data.message === "string") return data.message;
    if (typeof data.error === "string") return data.error;
  }
  return typeof message === "string" ? message : null;
}

export function SubmitEditCaseRate({ id }: { id: string }) {
  const {
    getValues,
    formState: { isValid },
  } = useFormContext();
  const navigate = useNavigate();
  const [useUpdateCaseRateRuleset] = useUpdateCaseRateRulesetMutation();
  const { hasConflicts } = useCaseRateCptConflicts({ excludeRulesetId: id });

  const { current: toastElement } = useContext(ToastContext);

  const goBackToList = () => navigate("/payer-rules");

  const { openForId, modal } = usePropagateAfterEdit({
    useLazyPreview: useLazyGetCaseRatePropagationPreviewQuery as any,
    useApplyPropagation: useApplyCaseRatePropagationMutation as any,
    onDone: goBackToList,
  });

  const updatePayerRule = async () => {
    const values = getValues();
    let cptCodes: string[];
    let excludedCptCodes: string[];
    try {
      cptCodes = prepareCodesForSubmit(values.cptCodes ?? []);
      excludedCptCodes = prepareCodesForSubmit(values.excludedCptCodes ?? []);
    } catch (err) {
      toastElement?.show({
        severity: "error",
        summary: "Invalid code range",
        detail:
          err instanceof CodeRangeError
            ? err.message
            : "Failed to expand code ranges",
      });
      return;
    }
    const requestBody = mapCaseRateFormToRequestBody({
      ...values,
      cptCodes,
      excludedCptCodes,
    });
    try {
      useUpdateCaseRateRuleset({ ...requestBody, id })
        .unwrap()
        .then(() => {
          toastElement?.show({
            severity: "success",
            summary: "Payer Ruleset updated",
            detail: "Payer Ruleset updated successfully",
          });
          if (requestBody.isDefault) {
            openForId(id);
          } else {
            goBackToList();
          }
        })
        .catch((error) => {
          if (error.status === 409) {
            toastElement?.show({
              severity: "error",
              summary: "Error",
              detail:
                "A case rate ruleset with this provider, payer, and rule name already exists",
            });
          } else if (error.status === 400) {
            const message = extractServerErrorMessage(error);
            toastElement?.show({
              severity: "error",
              summary: "Error",
              detail:
                message ||
                "An error has occurred attempting to update the discount ruleset",
            });
          } else {
            toastElement?.show({
              severity: "error",
              summary: "Error",
              detail:
                "An error has occurred attempting to update the discount ruleset",
            });
          }
        });
    } catch (error) {
      console.error(error);
    }
  };
  return (
    <ItemBar>
      <div
        data-testid="submit__Hospital"
        className="flex flex-1 justify-content-end"
      >
        <Button
          label="Update Payer Rule"
          onClick={updatePayerRule}
          disabled={!isValid || hasConflicts}
        />
      </div>
      {modal}
    </ItemBar>
  );
}
